import {
	createProductSpecificAnalyticsIntegrationPayload,
	ErrorType,
	Severity,
	Product,
	Team,
} from '@atlassian/growth-commons-error-analytics';

import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';
import { useQueryInterop } from '@confluence/apollo-relay-interop/entry-points/useQueryInterop.ts';
import { ConfluenceEdition } from '@confluence/change-edition/entry-points/ConfluenceEdition.ts';
import { getAGGClient } from '@confluence/graphql/entry-points/getAGGClient.ts';

import { addAtlOriginToUrl } from '../../utils/addAtlOriginToUrl.ts';

import type {
	ConfluencePricingEndOfTrialPremiumToStandardQuery$data,
	ConfluencePricingEndOfTrialPremiumToStandardQuery$variables,
} from './__generated__/ConfluencePricingEndOfTrialPremiumToStandardQuery.graphql.ts';
import { ConfluencePricingEndOfTrialPremiumToStandardQuery as PricingQuery } from './ConfluencePricingEndOfTrialPremiumToStandardQuery.ts';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';

type usePricingEndOfTrialData = {
	experienceUrl: string;
	standardPrice?: number | null;
	premiumPrice?: number | null;
	userCanAddPaymentDetails: boolean;
};

export function usePricingEOTPremiumToStandardQuery(skip?: boolean): {
	data: usePricingEndOfTrialData | null;
	loading: boolean;
	error?: Error;
} {
	const { cloudId, isLoggedIn, edition } = useSessionData();
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const canSkipQuery =
		skip || !cloudId || !isLoggedIn || !edition || edition !== ConfluenceEdition.PREMIUM;

	const { data, error, loading } = useQueryInterop<
		ConfluencePricingEndOfTrialPremiumToStandardQuery$data,
		ConfluencePricingEndOfTrialPremiumToStandardQuery$variables
	>(PricingQuery, {
		variables: {
			cloudId,
			productKey: 'confluence.ondemand',
		},
		skip: canSkipQuery,
		fetchPolicy: 'cache-first', // for SSR'ing purposes
		client: getAGGClient(),
		onError: (error: Error) => {
			const eventPayload = createProductSpecificAnalyticsIntegrationPayload({
				product: Product.CONFLUENCE,
				team: Team.GROWTH_LUNA,
				experimentMeta: {
					experimentKey: 'twcg-530_confluence_ai_in_editor_eot',
				},
				errorType: ErrorType.GRAPHQL,
				errorMessage: error?.message,
				severity: Severity.MEDIUM,
				source: 'usePricingEOTPremiumToStandardQuery',
				additionalAttributes: {
					cloudId,
				},
			});

			createAnalyticsEvent(eventPayload).fire();
		},
	});

	const entitlement = data?.tenantContexts?.[0]?.entitlementInfo?.entitlement;

	const isAvailableToUser =
		entitlement?.experienceCapabilities?.changeOfferingV2?.isAvailableToUser ?? false;

	const transactionAccount = entitlement?.transactionAccount;

	const canPay = transactionAccount?.isCurrentUserBillingAdmin === true;
	const hasPaymentDetails = transactionAccount?.isBillToPresent;

	const shouldSkipDueToPaymentDetails = !canPay || hasPaymentDetails;

	if (canSkipQuery || !entitlement || !isAvailableToUser || shouldSkipDueToPaymentDetails) {
		return {
			data: null,
			loading,
			error,
		};
	}

	const experienceUrl = addAtlOriginToUrl(
		entitlement?.experienceCapabilities?.changeOfferingV2?.experienceUrl ?? '',
	);
	let standardEditionPrice;
	let premiumEditionPrice;

	if (!canSkipQuery && entitlement) {
		const premiumEditionOrder = entitlement?.subscription?.trial;
		premiumEditionPrice = premiumEditionOrder?.listPriceEstimates?.find(
			(item) => item?.item?.chargeElement === 'user',
		)?.averageAmountPerUnit;

		const standardEditionOrder = entitlement?.standardPrice?.find(
			(orderItem) =>
				orderItem?.listPriceForOrderWithDefaults &&
				orderItem?.listPriceForOrderWithDefaults?.length > 0,
		);

		standardEditionPrice = standardEditionOrder?.listPriceForOrderWithDefaults?.find(
			(item) => item?.item?.chargeElement === 'user',
		)?.averageAmountPerUnit;
	}

	return {
		loading,
		error,
		data: {
			userCanAddPaymentDetails: canPay && !hasPaymentDetails,
			experienceUrl,
			standardPrice: standardEditionPrice && standardEditionPrice / 100,
			premiumPrice: premiumEditionPrice && premiumEditionPrice / 100,
		},
	};
}
